import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../utils/apiClient';
import { useTenant } from '../contexts/TenantContext';

interface TranslatedCategory {
    id: string;
    originalName: string;
    translatedName: string;
    originalDescription?: string;
    translatedDescription?: string;
}

interface TranslatedMenuItem {
    id: string;
    originalName: string;
    translatedName: string;
    originalDescription?: string;
    translatedDescription?: string;
    price?: number;
    categoryId?: string;
}

interface MenuTranslation {
    id: number;
    language: string;
    createdAt: string;
    updatedAt: string;
    categories: TranslatedCategory[];
    menuItems: TranslatedMenuItem[];
}

interface UseMenuTranslationsReturn {
    availableLanguages: string[];
    currentLanguage: string;
    isLoading: boolean;
    error: string | null;
    setLanguage: (language: string) => void;
    getTranslatedCategoryName: (originalName: string) => string;
    getTranslatedMenuItemName: (originalName: string) => string;
    getTranslatedMenuItemDescription: (originalDescription: string) => string;
    translation: MenuTranslation | null;
}

export const useMenuTranslations = (): UseMenuTranslationsReturn => {
    const { tenantId, reloadData } = useTenant() as any;
    const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
    const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
        // Recupera la lingua salvata o usa quella del browser/italiano
        const saved = localStorage.getItem('menu-language');
        if (saved && ['it', 'en', 'de', 'fr', 'es'].includes(saved)) {
            return saved;
        }

        const browserLang = navigator.language.split('-')[0];
        if (['it', 'en', 'de', 'fr', 'es'].includes(browserLang)) {
            return browserLang;
        }

        return 'it'; // Default italiano
    });
    const [translation, setTranslation] = useState<MenuTranslation | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const translationCache = useRef<Record<string, MenuTranslation | 'missing' | null>>({});

    // Carica le lingue disponibili
    const loadAvailableLanguages = useCallback(async () => {
        if (!tenantId) return;

        try {
            const response = await apiFetch(`/api/menu/translations/languages`, {
                tenantId: tenantId,
                cache: 'no-cache'
            });

            if (response.ok) {
                const data = await response.json();
                const languages = data.languages || [];

                // Aggiungi sempre l'italiano come lingua base
                const allLanguages = ['it', ...languages.filter((lang: string) => lang !== 'it')];
                setAvailableLanguages(allLanguages);
            }
        } catch (err) {
            console.error('Errore nel caricamento delle lingue disponibili:', err);
            setAvailableLanguages(['it']); // Fallback solo italiano
        }
    }, [tenantId]);

    // Carica la traduzione per la lingua corrente
    const loadTranslation = useCallback(async (language: string) => {
        if (!tenantId) return;

        if (language === 'it') {
            translationCache.current['it'] = null;
            setTranslation(null);
            setError(null);
            setIsLoading(false);
            return;
        }

        const cached = translationCache.current[language];
        if (cached !== undefined) {
            setIsLoading(false);
            if (cached === 'missing' || cached === null) {
                setTranslation(null);
                setError(`Traduzione non disponibile per la lingua ${language.toUpperCase()}`);
            } else {
                setTranslation(cached);
                setError(null);
            }
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await apiFetch(`/api/menu/translations/${language}`, {
                tenantId: tenantId,
                cache: 'no-cache'
            });

            if (response.ok) {
                const translationData = await response.json();
                translationCache.current[language] = translationData;
                setTranslation(translationData);
            } else if (response.status === 404) {
                translationCache.current[language] = 'missing';
                setTranslation(null);
                setError(`Traduzione non disponibile per la lingua ${language.toUpperCase()}`);
            } else {
                throw new Error(`Errore nel caricamento della traduzione: ${response.status}`);
            }
        } catch (err: any) {
            console.error('Errore nel caricamento della traduzione:', err);
            translationCache.current[language] = 'missing';
            setError(err.message || 'Errore nel caricamento della traduzione');
            setTranslation(null);
        } finally {
            setIsLoading(false);
        }
    }, [tenantId]);

    // Cambia lingua
    const setLanguage = useCallback((language: string) => {
        const cachedBefore = translationCache.current[language];
        setCurrentLanguage(language);
        localStorage.setItem('menu-language', language);
        loadTranslation(language);

        // Ricarica i dati del tenant per ottenere le traduzioni dal backend
        if (reloadData) {
            const needsReload = cachedBefore === undefined;
            if (needsReload) {
                setTimeout(() => {
                    reloadData();
                }, 100); // Piccolo delay per permettere al localStorage di essere aggiornato
            }
        }
    }, [loadTranslation, reloadData]);

    // Funzioni di traduzione
    const getTranslatedCategoryName = useCallback((originalName: string): string => {
        if (!translation || currentLanguage === 'it') return originalName;

        const translatedCategory = translation.categories.find(
            cat => cat.originalName === originalName
        );

        return translatedCategory?.translatedName || originalName;
    }, [translation, currentLanguage]);

    const getTranslatedMenuItemName = useCallback((originalName: string): string => {
        if (!translation || currentLanguage === 'it') return originalName;

        const translatedItem = translation.menuItems.find(
            item => item.originalName === originalName
        );

        return translatedItem?.translatedName || originalName;
    }, [translation, currentLanguage]);

    const getTranslatedMenuItemDescription = useCallback((originalDescription: string): string => {
        if (!translation || currentLanguage === 'it' || !originalDescription) return originalDescription;

        const translatedItem = translation.menuItems.find(
            item => item.originalDescription === originalDescription
        );

        return translatedItem?.translatedDescription || originalDescription;
    }, [translation, currentLanguage]);

    // Effetti
    useEffect(() => {
        if (tenantId) {
            loadAvailableLanguages();
            translationCache.current = {};
        }
    }, [tenantId, loadAvailableLanguages]);

    useEffect(() => {
        if (tenantId && currentLanguage) {
            loadTranslation(currentLanguage);
        }
    }, [tenantId, currentLanguage, loadTranslation]);

    return {
        availableLanguages,
        currentLanguage,
        isLoading,
        error,
        setLanguage,
        getTranslatedCategoryName,
        getTranslatedMenuItemName,
        getTranslatedMenuItemDescription,
        translation
    };
};